import type { AdminConsoleMeta, ManagedAdmin, ManagedDomain, ManagedRole, ManagedUser } from "../../types";
import { DEFAULT_FORM_STATE, type FormState, type ModalMode, type SelectedRecord } from "./constants";

export function buildEmptyForm(mode: ModalMode, meta: AdminConsoleMeta | null): FormState {
  const base = structuredClone(DEFAULT_FORM_STATE[mode]);
  if (mode === "roles") {
    (meta?.backendRoleFields || []).forEach((field) => {
      base[field] = false;
    });
  }
  return base;
}

export function normalizeRecordToForm(
  mode: ModalMode,
  record: SelectedRecord,
  meta: AdminConsoleMeta | null,
): FormState {
  if (!record) return buildEmptyForm(mode, meta);

  if (mode === "domains") {
    return {
      code: (record as ManagedDomain).code || "",
      name: record.name || "",
      description: (record as ManagedDomain).description || "",
      url: (record as ManagedDomain).url || "",
      allowedRedirectOrigins: (record as ManagedDomain).allowedRedirectOrigins || [],
      icon: (record as ManagedDomain).icon || "",
      isActive: (record as ManagedDomain).isActive ?? true,
    };
  }

  if (mode === "roles") {
    const data = { ...(((record as ManagedRole).data || {}) as Record<string, unknown>) };
    return {
      name: record.name || "",
      level: (record as ManagedRole).level ?? Number(data.level || 5),
      ...data,
    };
  }

  return {
    name: record.name || "",
    login: (record as ManagedUser | ManagedAdmin).login || "",
    email: (record as ManagedUser | ManagedAdmin).email || "",
    phone: (record as ManagedUser | ManagedAdmin).phone || "",
    password: "",
    systemIds: [
      ...((record as ManagedUser | ManagedAdmin).systemIds ||
        (record as ManagedUser | ManagedAdmin).domains?.map((d) => d.id).filter(Boolean) ||
        []),
    ],
    ...(mode === "admins"
      ? {
          roleId: (record as ManagedAdmin).roleId || undefined,
          level: (record as ManagedAdmin).level ?? 5,
          readonly: !!(record as ManagedAdmin).readonly,
        }
      : {}),
  };
}

export function buildPayloadFromForm(mode: ModalMode, values: FormState) {
  if (mode === "domains") {
    const origins = (values.allowedRedirectOrigins || [])
      .map((value: string) => (value || "").trim())
      .filter((value: string) => value.length > 0);
    return {
      code: values.code,
      name: values.name,
      description: values.description || undefined,
      url: values.url,
      allowedRedirectOrigins: origins,
      icon: values.icon || undefined,
      isActive: !!values.isActive,
    };
  }

  if (mode === "roles") return values;

  return {
    name: values.name,
    login: values.login,
    email: values.email || undefined,
    phone: values.phone || undefined,
    password: values.password || undefined,
    systemIds: values.systemIds || [],
    ...(mode === "admins"
      ? {
          roleId: values.roleId || "",
          level: Number(values.level || 5),
          readonly: !!values.readonly,
        }
      : {}),
  };
}

export function formatDate(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export function singularize(value: string) {
  return value.endsWith("s") ? value.slice(0, -1) : value;
}

export function toLabel(value: string) {
  return value.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());
}
